
import HeroSection from '@/components/ui-custom/HeroSection';
import ContactForm from '@/components/ui-custom/ContactForm';
import { Mail, MapPin, Users, MessageSquare } from 'lucide-react';

const Contact = () => {
  const showContactForm = false;
  const renderContactForm = () => {
    return showContactForm ? (
      <div className="lg:col-span-2">
        <h2 className="heading-lg mb-6">Send Us a Message</h2>
        <ContactForm />
      </div>
    ) : null;
  };

  return (
    <div className="min-h-screen">
      <HeroSection
        title="Contact Us"
        subtitle="Get in touch with the Exodus ADV team. We'd love to hear from you."
        backgroundImage="/images/contact-hero.jpg"
      />
      
      <main id="content" className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 {(showContactForm? 'lg:grid-cols-3' : ''} gap-8">
            {renderContactForm()}
            
            {/* Contact Info */}
            <div>
              <h2 className="heading-lg mb-6">Connect With Us</h2>
              
              {/* Contact Cards */}
              <div className="space-y-4 mb-8">
                <div className="glass-card rounded-lg p-5">
                  <div className="flex items-start">
                    <div className="bg-adv-orange/20 rounded-full p-3 mr-4">
                      <Mail className="text-adv-orange" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Email Us</h3>
                      <p className="text-adv-lightGray mb-2">
                        For general inquiries:
                      </p>
                      <a href="mailto:ward.kennedy@exodusadv.com" className="text-adv-orange hover:underline">
                        ward.kennedy@exodusadv.com
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="glass-card rounded-lg p-5">
                  <div className="flex items-start">
                    <div className="bg-adv-orange/20 rounded-full p-3 mr-4">
                      <MapPin className="text-adv-orange" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Our Base</h3>
                      <p className="text-adv-lightGray">
                        While our events happen across the country, we're based in:<br />
                        Nashville, Tennessee
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="glass-card rounded-lg p-5">
                  <div className="flex items-start">
                    <div className="bg-adv-orange/20 rounded-full p-3 mr-4">
                      <MessageSquare className="text-adv-orange" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Response Time</h3>
                      <p className="text-adv-lightGray">
                        We aim to respond to all inquiries within 24-48 hours during business days.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sponsorship */}
              <div className="glass-card rounded-lg p-6 border border-adv-orange/30 mb-8">
                <h3 className="font-bold text-lg mb-3">Sponsorship Opportunities</h3>
                <p className="text-adv-lightGray mb-4">
                  Interested in partnering with Exodus ADV? We offer various sponsorship opportunities for brands aligned with the adventure motorcycle community.
                </p>
                <a href="mailto:sponsors@exodusadv.com" className="btn-primary w-full text-center">
                  Sponsor Inquiry
                </a>
              </div>
              
              {/* Social Media */}
              <div>
                <h3 className="font-bold text-lg mb-4">Follow Us</h3>
                <div className="flex space-x-3">
                  <a 
                    href="https://www.instagram.com/Exodusadv/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-adv-charcoal hover:bg-adv-darkGray transition-colors w-12 h-12 rounded-full flex items-center justify-center"
                    aria-label="Instagram"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-adv-orange">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  </a>
                  <a 
                    href="https://www.youtube.com/@exodusadv" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-adv-charcoal hover:bg-adv-darkGray transition-colors w-12 h-12 rounded-full flex items-center justify-center"
                    aria-label="YouTube"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-adv-orange">
                      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                      <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* FAQ */}
          <section className="mt-16">
            <div className="text-center mb-12">
              <h2 className="heading-lg mb-4">Frequently Asked Questions</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                Quick answers to common questions.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="glass-card rounded-lg p-5">
                <h3 className="font-bold mb-2">How do I register for a rally?</h3>
                <p className="text-adv-lightGray">
                  Visit our Rallies page to see upcoming events. Each rally has a registration button that will take you through the sign-up process.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5">
                <h3 className="font-bold mb-2">Can I submit a ride even if I'm new?</h3>
                <p className="text-adv-lightGray">
                  Absolutely! The Ride Hub is open to everyone. Just make sure to accurately describe the difficulty level so riders know what to expect.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5">
                <h3 className="font-bold mb-2">How can I be featured in your media?</h3>
                <p className="text-adv-lightGray">
                  We're always looking for great stories. Send us an email with your adventure details, and our media team will review it for potential feature opportunities.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5">
                <h3 className="font-bold mb-2">Do you offer refunds for rally registrations?</h3>
                <p className="text-adv-lightGray">
                  We offer full refunds up to 30 days before the event, and 50% refunds up to 14 days before. After that, registrations are non-refundable but can be transferred.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Contact;
